import { Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, map } from 'rxjs';
import {
	BaseFormlyStaticProp,
	FormlyInputProp,
	FormlyCheckProp,
	FormlyDateProp,
	FormlyHiddenProp,
	FormlyRadioProp,
	FormlySelectProp,
	FormlyTextareaProp,
	FormlyChipsAutoCompleteProp,
	ApiUrlResults,
	OptionValueProp,
	FormlySetterProp,
	FormlyFileUploadProp
} from '@nx-ahs/formly/shared';
import {
	AddFieldDialogComponent,
	AddFieldDialogData
} from '../components/visual-builder/add-field-dialog/add-field-dialog.component';

export interface BlockType {
	name: string;
	category: 'component' | 'node' | 'custom';
	click: () => void;
}

@Injectable({
	providedIn: 'root'
})
export class BuilderService {
	// These items in here are what are shown in the 'Quick Add' dropdown
	public blocks: BlockType[] = [
		{
			name: 'static',
			category: 'component',
			click: this.addStatic.bind(this)
		},
		{
			name: 'input',
			category: 'component',
			click: this.addInput.bind(this)
		},
		{
			name: 'address',
			category: 'component',
			click: this.addAddress.bind(this)
		},
		{
			name: 'chips-autocomplete',
			category: 'component',
			click: this.addChipsAutocomplete.bind(this)
		},
		{
			name: 'chips',
			category: 'component',
			click: this.addChips.bind(this)
		},
		{
			name: 'checkbox',
			category: 'component',
			click: this.addCheck.bind(this)
		},
		{
			name: 'date',
			category: 'component',
			click: this.addDate.bind(this)
		},
		{
			name: 'group',
			category: 'component',
			click: this.addGroup.bind(this)
		},
		{
			name: 'file-upload',
			category: 'component',
			click: this.addFileUpload.bind(this)
		},
		{
			name: 'hidden',
			category: 'component',
			click: this.addHidden.bind(this)
		},
		{
			name: 'radio',
			category: 'component',
			click: this.addRadio.bind(this)
		},
		{
			name: 'repeater',
			category: 'component',
			click: this.addRepeater.bind(this)
		},
		{
			name: 'select',
			category: 'component',
			click: this.addSelect.bind(this)
		},
		{
			name: 'textarea',
			category: 'component',
			click: this.addTextarea.bind(this)
		},
		{
			name: 'setter',
			category: 'component',
			click: this.addSetter.bind(this)
		},
		{
			name: 'api',
			category: 'node',
			click: this.addApiJson.bind(this)
		},
		{
			name: 'option',
			category: 'node',
			click: this.addArrayOption.bind(this)
		}
	];

	constructor(private dialog: MatDialog) {}

	/**
	 * Opens the Add Field dialog and returns an Observable with the created field.
	 * Returns null if the user cancels the dialog.
	 * @param fieldType The type of field to create (e.g., 'input', 'select', 'group')
	 * @param existingKeys Array of existing field keys for validation
	 * @returns Observable<FormlyFieldConfig | null>
	 */
	public openAddFieldDialog(fieldType: string, existingKeys: string[]): Observable<FormlyFieldConfig | null> {
		const fieldTypeLabel = this.getFieldTypeLabel(fieldType);

		const dialogRef = this.dialog.open(AddFieldDialogComponent, {
			width: '650px',
			maxWidth: '90vw',
			disableClose: true,
			autoFocus: true,
			data: {
				fieldType: fieldType,
				fieldTypeLabel: fieldTypeLabel,
				existingKeys: existingKeys
			} as AddFieldDialogData
		});

		return dialogRef.afterClosed().pipe(
			map((result) => {
				if (!result) {
					return null;
				}

				// Create the field using the appropriate method
				const newField = this.createFieldByType(fieldType);
				newField.key = result.key;

				// Initialize props if not exists
				if (!newField.props) {
					newField.props = {};
				}

				// Apply all properties from the dialog
				newField.props.label = result.label;
				if (result.placeholder) {
					newField.props.placeholder = result.placeholder;
				}
				if (result.description) {
					newField.props.description = result.description;
				}
				if (result.required) {
					newField.props.required = result.required;
				}
				if (result.disabled) {
					newField.props.disabled = result.disabled;
				}

				return newField;
			})
		);
	}

	/**
	 * Creates a field configuration by type using the appropriate builder method
	 */
	public createFieldByType(type: string): FormlyFieldConfig {
		const methodMap: Record<string, () => FormlyFieldConfig> = {
			static: () => this.addStatic(),
			input: () => this.addInput(),
			textarea: () => this.addTextarea(),
			select: () => this.addSelect(),
			checkbox: () => this.addCheck(),
			radio: () => this.addRadio(),
			date: () => this.addDate(),
			hidden: () => this.addHidden(),
			richtext: () => this.addRichText(),
			chips: () => this.addChips(),
			'chips-autocomplete': () => this.addChipsAutocomplete(),
			address: () => this.addAddress(),
			'file-upload': () => this.addFileUpload(),
			group: () => this.addGroup(),
			repeater: () => this.addRepeater(),
			'nested-script': () => this.addNestedScript(),
			setter: () => this.addSetter()
		};

		const method = methodMap[type];
		if (method) {
			return method();
		}

		// Fallback for unknown types
		return {
			key: 'newField',
			type: `mat-formly-${type}`,
			props: {
				label: 'New Field'
			}
		};
	}

	/**
	 * Recursively collects all field keys from a fields array
	 */
	public getAllFieldKeys(fields: FormlyFieldConfig[]): string[] {
		const keys: string[] = [];
		fields.forEach((field) => {
			if (field.key) {
				const keyString = Array.isArray(field.key) ? field.key.join('.') : String(field.key);
				keys.push(keyString);
			}
			// Recursively get keys from nested fields
			if (field.fieldGroup) {
				keys.push(...this.getAllFieldKeys(field.fieldGroup));
			}
		});
		return keys;
	}

	/**
	 * Gets a human-readable label for a field type
	 */
	public getFieldTypeLabel(type: string): string {
		const labelMap: Record<string, string> = {
			static: 'Static Text',
			input: 'Text Input',
			textarea: 'Text Area',
			select: 'Select',
			checkbox: 'Checkbox',
			radio: 'Radio',
			date: 'Date',
			hidden: 'Hidden',
			richtext: 'Rich Text',
			chips: 'Chips',
			'chips-autocomplete': 'Chips Autocomplete',
			address: 'Address',
			'file-upload': 'File Upload',
			group: 'Group',
			repeater: 'Repeater',
			'nested-script': 'Nested Script',
			setter: 'Setter'
		};
		return labelMap[type] || type;
	}

	public addRichText(key?: string): FormlyFieldConfig {
		return {
			key: key || 'replace',
			type: 'mat-formly-richtext',
			props: {
				label: null,
				placeholder: 'Enter text',
				required: false
			},
			hideExpression: null
		};
	}

	public addNestedScript(key?: string): FormlyFieldConfig {
		return {
			key: key || 'nestedScript',
			type: 'mat-formly-nested-script',
			props: {
				label: 'Nested Script',
				name: 'Nested Script Container',
				description: 'Container for executing multiple scripts sequentially',
				transactional: false,
				collectData: true,
				defaultViewMode: 'collapsed',
				executeNestedScriptsAfterSave: false,
				scripts: []
			}
		};
	}

	public addStatic(): FormlyFieldConfig {
		return {
			type: 'mat-formly-static',
			props: {
				name: null,
				description: null
			} as BaseFormlyStaticProp
		} as FormlyFieldConfig;
	}

	public addInput(key?: string): FormlyFieldConfig {
		return {
			key: key || 'replace',
			type: 'mat-formly-input',
			props: {
				inputType: 'text',
				name: null,
				description: null,
				label: null,
				placeholder: null,
				hint: null,
				required: null,
				disabled: false,
				max: null,
				maxlength: null,
				min: null,
				minlength: null,
				pattern: null,
				exclude: false,
				mask: null
			} as FormlyInputProp,
			hideExpression: null
		} as FormlyFieldConfig;
	}

	public addAddress(key?: string): FormlyFieldConfig {
		return {
			key: key || 'replace',
			type: 'mat-formly-address',
			hideExpression: null,
			fieldGroup: [
				{ key: 'AddressSearch', type: 'input' },
				{ key: 'AddressLine1', type: 'input' },
				{ key: 'AddressLine2', type: 'input' },
				{ key: 'City', type: 'input' },
				{ key: 'County', type: 'input' },
				{ key: 'State', type: 'input' },
				{ key: 'Zip', type: 'input' },
				{ key: 'Country', type: 'input' }
			],
			props: {
				label: null,
				description: null,
				inputType: 'text',
				name: 'myAddressField',
				isAddressRequired: false,
				showAutoFillAddressSearch: true,
				autoFillAddressInputPlaceHolder: 'Start typing address',
				showStreetAddress1: true,
				showStreetAddress2: false,
				showCity: true,
				showCounty: false,
				showCountry: false,
				showState: true,
				showZipCode: true
			}
		} as FormlyFieldConfig;
	}

	public addChipsAutocomplete(key?: string): FormlyFieldConfig {
		return {
			key: key || 'replace',
			type: 'mat-formly-chips-autocomplete',
			props: {
				inputType: 'text',
				name: null,
				placeholder: null,
				description: null,
				label: null,
				disabled: false,
				required: false,
				allowRemove: true,
				availableOptions: [
					{ label: 'Option 1', value: 'Option 1' },
					{ label: 'Option 2', value: 'Option 2' },
					{ label: 'Option 3', value: 'Option 3' }
				],
				api: null,
				allowCustomValue: false,
				maxSelections: 0
			} as FormlyChipsAutoCompleteProp,
			hideExpression: null
		} as FormlyFieldConfig;
	}

	public addCheck(key?: string): FormlyFieldConfig {
		return {
			key: key || 'replace',
			type: 'mat-formly-check',
			props: {
				name: null,
				description: null,
				options: null,
				api: null,
				single: null,
				required: null,
				disabled: false,
				exclude: false
			} as FormlyCheckProp,
			hideExpression: null
		} as FormlyFieldConfig;
	}

	public addChips(key?: string): FormlyFieldConfig {
		return {
			key: key || 'replace',
			type: 'mat-formly-chips',
			props: {
				name: null,
				label: null,
				description: null,
				placeholder: 'Add a chip...',
				required: false,
				disabled: false,
				allowRemove: false,
				defaultChips: ['item1', 'item2']
			},
			hideExpression: null
		} as FormlyFieldConfig;
	}

	public addDate(key?: string): FormlyFieldConfig {
		return {
			key: key || 'replace',
			type: 'mat-formly-date',
			props: {
				name: null,
				description: null,
				label: null,
				placeholder: null,
				hint: null,
				required: null,
				disabled: false,
				touchUi: false,
				minDate: null,
				maxDate: null,
				pattern: null,
				exclude: false
			} as FormlyDateProp,
			hideExpression: null
		} as FormlyFieldConfig;
	}

	public addGroup(key?: string): FormlyFieldConfig {
		return {
			type: 'mat-formly-group',
			props: {
				name: null,
				description: null,
				groupType: null,
				required: null,
				exclude: false,
				layout: {
					fxLayout: 'column',
					fxLayoutAlignHorizontal: null,
					fxLayoutAlignVertical: null,
					fxLayoutGap: '0px',
					fxFlex: null
				}
			} as FormlyRadioProp,
			fieldGroup: [],
			hideExpression: null
		} as FormlyFieldConfig;
	}

	public addFileUpload(key?: string): FormlyFieldConfig {
		return {
			key: key || 'replace',
			type: 'mat-formly-file-upload',
			props: {
				// BaseFormlyStaticProp properties (required)
				name: '', // provide a default value or update the interface to allow null
				description: '',
				required: false,
				// BaseFormFieldProp properties (optional)
				label: '',
				placeholder: 'Select files to upload',
				hint: 'Allowed file types: .pdf, .docx',
				// FormlyFileUploadProp specific properties
				multiple: true,
				accept: '.pdf,.docx'
			} as FormlyFileUploadProp,
			hideExpression: null
		} as FormlyFieldConfig;
	}

	public addHidden(key?: string): FormlyFieldConfig {
		return {
			key: key || 'replace',
			type: 'mat-formly-hidden',
			props: {
				fixedValue: null,
				api: {
					url: null,
					objectPath: null // "1.firstName"; // equivalent to results[1]['firstName']
				},
				exclude: false
			} as FormlyHiddenProp
		} as FormlyFieldConfig;
	}

	public addRadio(key?: string): FormlyFieldConfig {
		return {
			key: key || 'replace',
			type: 'mat-formly-radio',
			props: {
				name: null,
				description: null,
				options: null,
				api: null,
				required: null,
				disabled: false,
				exclude: false,
				labelPosition: 'after',
				color: null,
				layout: {
					fxLayout: 'column',
					fxLayoutAlignHorizontal: null,
					fxLayoutAlignVertical: null,
					fxLayoutGap: '0px',
					fxFlex: null
				}
			} as FormlyRadioProp,
			hideExpression: null
		} as FormlyFieldConfig;
	}

	public addRepeater(key?: string): FormlyFieldConfig {
		return {
			key: key || 'replace',
			type: 'mat-formly-repeater',
			props: {
				name: null,
				description: null,
				showAddAtTop: true,
				labels: {
					addButtonTitle: null,
					removeButtonTitle: null,
					totalTitle: null
				},
				arrayLimit: null,
				onLoadAddCount: null,
				disallowRemove: null,
				disallowAdd: null
			},
			fieldArray: {
				type: 'mat-formly-group',
				props: {
					name: null,
					description: null,
					groupType: null,
					exclude: false,
					layout: {
						fxLayout: 'column',
						fxLayoutAlignHorizontal: null,
						fxLayoutAlignVertical: null,
						fxLayoutGap: '0px',
						fxFlex: null
					}
				},
				fieldGroup: []
			},
			hideExpression: null
		} as FormlyFieldConfig;
	}

	public addSelect(key?: string): FormlyFieldConfig {
		return {
			key: key || 'replace',
			type: 'mat-formly-select',
			props: {
				name: null,
				description: null,
				label: null,
				placeholder: null,
				hint: null,
				options: null,
				api: null,
				multiple: false,
				includeEmpty: false,
				includeEmptyLabel: null,
				required: null,
				disabled: false,
				exclude: false
			} as FormlySelectProp,
			hideExpression: null
		} as FormlyFieldConfig;
	}

	public addTextarea(key?: string): FormlyFieldConfig {
		return {
			key: key || 'replace',
			type: 'mat-formly-textarea',
			props: {
				name: null,
				description: null,
				label: null,
				placeholder: null,
				hint: null,
				required: null,
				disabled: false,
				max: null,
				maxlength: null,
				min: null,
				minlength: null,
				pattern: null,
				exclude: false,
				cols: null,
				rows: null
			} as FormlyTextareaProp,
			hideExpression: null
		} as FormlyFieldConfig;
	}

	public addSetter(key?: string): FormlyFieldConfig {
		return {
			type: 'mat-formly-setter',
			props: {
				setters: [
					{
						key: null,
						type: 'api | global-javascript-variable | query-string | fixed-value' as any,
						overwrite: false,
						value: null,
						api: null,
						disabled: false
					}
				]
			} as FormlySetterProp
		} as FormlyFieldConfig;
	}

	public addApiJson(): ApiUrlResults {
		return {
			url: null,
			itemFieldLabel: null,
			itemFieldValue: null,
			override: false,
			refDataName: null,
			method: null,
			options: null
		};
	}

	public addArrayOption(): OptionValueProp {
		return {
			label: null,
			value: null,
			disabled: false
		};
	}
}
